// MissionThreadPanel — Feature D (Mission Thread map).
//
// The headline, deck-worthy figure: a clickable horizontal lane rendering a
// project's governed lineage —
//   intake → AI scoring → decision → project → gate → risk → action →
//   briefing product → closeout → archived corporate memory.
//
// Read-only. Nodes/edges are assembled server-side (GET /projects/:id/mission-thread);
// this component only renders them, reusing the app's card/chip vocabulary and the
// StagePipeline visual grammar (bubbles + directional connectors) rather than a new
// chart library. Reached stages render as clickable Links to their deep routes;
// unreached downstream stages render as dimmed "upcoming" placeholders so a leader
// sees where the effort sits on the journey.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArrowRight,
  ChevronDown,
  FileBarChart,
  FileText,
  FolderKanban,
  Gavel,
  Landmark,
  ListChecks,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { useApi } from '../../lib/useApi.js';
import {
  StatusChip,
  LoadingSkeleton,
  ErrorState,
  BrandedEmptyState,
} from '../../components/index.js';
import './missionThread.css';

// Canonical stage skeleton — the renderer overlays reached nodes onto this so
// unreached downstream stages become dimmed placeholders. Kept independent of the
// service (presentational concern), but the type order matches the server.
const MISSION_THREAD_STAGES = [
  { type: 'intake_request', label: 'Intake request', Icon: FileText },
  { type: 'ai_scoring_run', label: 'AI scoring', Icon: Sparkles },
  { type: 'intake_decision', label: 'Intake decision', Icon: Gavel },
  { type: 'project', label: 'Project', Icon: FolderKanban },
  { type: 'lifecycle_gate', label: 'Lifecycle gate', Icon: ShieldCheck },
  { type: 'risk', label: 'Risk', Icon: TriangleAlert },
  { type: 'action', label: 'Action', Icon: ListChecks },
  { type: 'briefing_product', label: 'Briefing product', Icon: FileBarChart },
  { type: 'closeout_package', label: 'Closeout', Icon: Archive },
  { type: 'corporate_memory', label: 'Corporate memory', Icon: Landmark },
];

// Governed edge relationship keyed by destination stage — mirrors the service so
// every connector (even those leading into an unreached placeholder) is styled.
const EDGE_KIND_BY_TARGET = {
  ai_scoring_run: 'derives',
  intake_decision: 'governs',
  project: 'derives',
  lifecycle_gate: 'governs',
  risk: 'governs',
  action: 'promotes',
  briefing_product: 'handoff',
  closeout_package: 'governs',
  corporate_memory: 'archives',
};

const EDGE_LABELS = {
  derives: 'derives',
  governs: 'governs',
  promotes: 'promotes',
  handoff: 'handoff',
  archives: 'archives',
};

function formatThreadDate(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  if (match) {
    const [, year, month, day] = match;
    return `${months[Number(month) - 1]} ${Number(day)}, ${year}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${months[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()}`;
}

function ThreadEdge({ kind, reached }) {
  return (
    <div
      className={`thread-edge thread-edge--${kind} ${reached ? 'is-reached' : 'is-upcoming'}`}
      data-kind={kind}
      aria-hidden
    >
      <span className="thread-edge__line" />
      <ArrowRight size={16} className="thread-edge__arrow" />
      <span className="thread-edge__label">{EDGE_LABELS[kind] || kind}</span>
    </div>
  );
}

function ReachedNode({ stage, node, tone }) {
  const { Icon } = stage;
  const count = node.meta?.count || 0;
  const handoffs = node.meta?.handoffs || [];
  const items = node.meta?.items || [];
  const grouped = count > 1;
  const [expanded, setExpanded] = useState(false);
  const timestamp = formatThreadDate(node.timestamp);

  return (
    <div
      className={`thread-node thread-node--${tone}`}
      data-testid={`mission-thread-node-${stage.type}`}
      data-node-type={stage.type}
    >
      <Link
        className="thread-node__main"
        to={node.route}
        data-testid={`mission-thread-link-${stage.type}`}
      >
        <span className={`thread-node__bubble tone-${tone}`} aria-hidden>
          <Icon size={18} />
        </span>
        <span className="thread-node__eyebrow">{stage.label}</span>
        <span className="thread-node__title" title={node.label}>
          {node.label}
        </span>
        <span className="thread-node__meta-row">
          <StatusChip status={node.status} />
          {grouped ? <span className="thread-node__count">×{count}</span> : null}
        </span>
        {timestamp ? <span className="thread-node__timestamp">{timestamp}</span> : null}
        {handoffs.length ? (
          <span
            className="thread-node__handoff"
            data-testid={`mission-thread-handoff-${stage.type}`}
          >
            {handoffs.length} handoff{handoffs.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </Link>
      {grouped ? (
        <button
          type="button"
          className={`thread-node__more ${expanded ? 'is-open' : ''}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          data-testid={`mission-thread-expand-${stage.type}`}
        >
          <ChevronDown size={14} aria-hidden />
          {expanded ? 'Hide' : `${count} items`}
        </button>
      ) : null}
      {grouped && expanded ? (
        <ul className="thread-node__items" data-testid={`mission-thread-items-${stage.type}`}>
          {items.map((item) => (
            <li key={item.id}>
              <Link className="thread-node__item-link" to={item.route}>
                <span className="thread-node__item-label" title={item.label}>
                  {item.label}
                </span>
                <StatusChip status={item.status} />
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function UpcomingNode({ stage }) {
  const { Icon } = stage;
  return (
    <div
      className="thread-node thread-node--upcoming"
      data-testid={`mission-thread-placeholder-${stage.type}`}
      data-node-type={stage.type}
      aria-label={`${stage.label} — not yet reached`}
    >
      <div className="thread-node__main is-placeholder">
        <span className="thread-node__bubble tone-upcoming" aria-hidden>
          <Icon size={18} />
        </span>
        <span className="thread-node__eyebrow">{stage.label}</span>
        <span className="thread-node__title thread-node__title--muted">Not yet reached</span>
      </div>
    </div>
  );
}

export function MissionThreadPanel({ projectId }) {
  // Self-contained + deep-linkable at /projects/:id?tab=journey. Read-only fetch
  // of the server-assembled thread; no mutations.
  const threadApi = useApi(`/projects/${projectId}/mission-thread`, { skip: !projectId });
  const thread = threadApi.data?.data || null;
  const loading = threadApi.loading;
  const error = threadApi.error;

  if (loading) return <LoadingSkeleton rows={4} />;
  if (error) return <ErrorState error={error} />;

  const nodes = thread?.nodes || [];
  const meta = thread?.meta || {};

  if (!nodes.length) {
    return (
      <div className="profile-tab" style={{ marginTop: 16 }} data-testid="mission-thread-empty">
        <BrandedEmptyState
          eyebrow="Mission thread"
          title="No mission thread yet"
          description="This project has no governed lineage records to trace. Once intake, gates, risks, actions, or products are recorded, the mission thread will assemble here."
        />
      </div>
    );
  }

  const nodeByType = new Map(nodes.map((node) => [node.type, node]));
  // The active (current) stage is the last reached node in the canonical order.
  const reachedTypes = MISSION_THREAD_STAGES.filter((stage) => nodeByType.has(stage.type)).map(
    (stage) => stage.type,
  );
  const activeType = reachedTypes[reachedTypes.length - 1] || null;

  return (
    <div
      className="profile-tab mission-thread-panel"
      style={{ marginTop: 16 }}
      data-testid="mission-thread"
    >
      <div className="mission-thread-header">
        <div>
          <div className="eyebrow">Mission thread</div>
          <h3 className="mission-thread-title">Governed lineage from intake to corporate memory</h3>
          <p className="muted small mission-thread-subtitle">
            {meta.hasIntakeLineage
              ? 'Every node is a governed record. Select any node to open its source.'
              : 'No intake lineage recorded — this project was created directly, so the thread begins at the project node.'}
          </p>
        </div>
        <div className="mission-thread-legend" aria-hidden>
          <StatusChip status={meta.complete ? 'approved' : 'inProgress'}>
            {meta.complete ? 'Archived — thread complete' : 'In flight'}
          </StatusChip>
          <span className="chip outline sm">
            {reachedTypes.length}/{MISSION_THREAD_STAGES.length} stages reached
          </span>
        </div>
      </div>

      <div className="mission-thread-scroller">
        <ol className="mission-thread" role="list" data-testid="mission-thread-lane">
          {MISSION_THREAD_STAGES.map((stage, index) => {
            const node = nodeByType.get(stage.type);
            const previous = MISSION_THREAD_STAGES[index - 1];
            const edgeReached = Boolean(node) && Boolean(previous && nodeByType.has(previous.type));
            const tone = node ? (stage.type === activeType ? 'active' : 'done') : 'upcoming';
            return (
              <li className="mission-thread-cell" key={stage.type}>
                {index > 0 ? (
                  <ThreadEdge
                    kind={EDGE_KIND_BY_TARGET[stage.type] || 'derives'}
                    reached={edgeReached}
                  />
                ) : null}
                {node ? (
                  <ReachedNode stage={stage} node={node} tone={tone} />
                ) : (
                  <UpcomingNode stage={stage} />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
